import SnoozeIcon from '@mui/icons-material/Snooze';
import { Button, Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import type { MouseEvent } from 'react';

import type { LiftAIGridColDef } from '~/components/table/colDefTemplate';
import type { ActionItem, ActionItemEventType } from '~/data/schemas/actionItem';
import type { LocaleTerms } from '~/hooks/useLocaleTerms';

import { SEVERITY_LABEL, SEVERITY_ORDER, SeverityIcon, severityRank } from './severity';
import {
  displayTitle,
  EM_DASH,
  eventTypeCategory,
  formatAge,
  formatValueAtStake,
  getEventTypeLabels,
  SUGGESTED_ACTION_COLOR,
  SUGGESTED_ACTION_LABEL,
  SUGGESTION_OPTIONS,
} from './workQueueItem';

type ColumnArgs = {
  currencyCode: string;
  terms: LocaleTerms;
  onSnooze: (item: ActionItem, event: MouseEvent<HTMLElement>) => void;
  onReview: (item: ActionItem) => void;
  tasksEnabled: boolean;
};

/**
 * Every column centres vertically. `display: 'flex'` is what buys it: rows are
 * auto-height, so without it a one-line cell sits at the top of a row a three-line Item
 * cell made tall.
 */
const V_CENTERED = { display: 'flex' } as const satisfies Partial<LiftAIGridColDef<ActionItem>>;

/** Centred both ways — the short, glanceable columns. */
const CENTERED = {
  ...V_CENTERED,
  align: 'center',
  headerAlign: 'center',
} as const satisfies Partial<LiftAIGridColDef<ActionItem>>;

/** Centred vertically, text at the start — the reading columns, where centred prose leaves no common edge to run the eye down. */
const TEXT_LEADING = {
  ...V_CENTERED,
  align: 'left',
  headerAlign: 'left',
} as const satisfies Partial<LiftAIGridColDef<ActionItem>>;

/** Stacked cells: fill the cell so the text has a width to align inside. */
const STACKED_CELL_SX = { width: '100%', minWidth: 0, py: 0.5 } as const;

/** …and its centred counterpart, for the value column. */
const STACKED_CELL_CENTERED_SX = { ...STACKED_CELL_SX, textAlign: 'center' } as const;

/**
 * The Work Queue's column set.
 *
 * Severity, Type and AI Suggestion are `singleSelect` on purpose: that is what turns
 * them into dropdowns in the grid's Filters panel, which is where the page's three
 * bespoke `Select`s went. AI Suggestion ships hidden, but a hidden column still
 * filters, so the control survives the column coming off the table.
 */
export function getWorkQueueColumns({
  currencyCode,
  terms,
  onSnooze,
  onReview,
  tasksEnabled,
}: ColumnArgs): LiftAIGridColDef<ActionItem>[] {
  const eventTypeLabels = getEventTypeLabels(terms);
  const filterableEventTypes = (Object.keys(eventTypeLabels) as ActionItemEventType[]).filter(
    (value) => tasksEnabled || !value.startsWith('task.'),
  );

  return [
    {
      field: 'severity',
      headerName: 'Severity',
      type: 'singleSelect',
      width: 110,
      ...CENTERED,
      valueOptions: SEVERITY_ORDER.map((value) => ({ value, label: SEVERITY_LABEL[value] })),
      // `singleSelect` would otherwise sort the raw values alphabetically, which
      // puts critical, info, warning in that order — a ranking nobody means.
      sortComparator: (a, b) => severityRank(a) - severityRank(b),
      renderCell: ({ row }) => <SeverityIcon severity={row.severity} />,
    },
    {
      field: 'title',
      headerName: 'Item',
      flex: 2,
      minWidth: 300,
      ...TEXT_LEADING,
      // The description shows in the cell, so it belongs in the value the search box and the
      // "Item contains" filter look at.
      valueGetter: (_, row) =>
        [displayTitle(row, terms), row.description].filter(Boolean).join(' — '),
      renderCell: ({ row }) => (
        <Stack sx={STACKED_CELL_SX}>
          {row.suggestedAction ? (
            <Chip
              size="small"
              variant="outlined"
              color={SUGGESTED_ACTION_COLOR[row.suggestedAction]}
              label={`CONTRACT ANALYSIS (AI): ${SUGGESTED_ACTION_LABEL[row.suggestedAction]}`}
              sx={{
                mb: 0.5,
                height: 20,
                alignSelf: 'flex-start',
                '& .MuiChip-label': { px: 1, fontSize: '0.7rem' },
              }}
            />
          ) : null}
          <Typography variant="body2" fontWeight={500} noWrap>
            {displayTitle(row, terms)}
          </Typography>
          {row.description ? (
            <Typography variant="caption" color="text.secondary" noWrap>
              {row.description}
            </Typography>
          ) : null}
        </Stack>
      ),
    },
    {
      field: 'portfolio',
      headerName: 'Client',
      flex: 1,
      minWidth: 150,
      ...TEXT_LEADING,
      valueGetter: (_, row) => row.portfolio?.name ?? '',
      renderCell: ({ value }) => (value ? String(value) : EM_DASH),
    },
    {
      field: 'eventType',
      headerName: 'Type',
      type: 'singleSelect',
      width: 190,
      ...TEXT_LEADING,
      valueOptions: filterableEventTypes.map((value) => ({
        value,
        label: eventTypeLabels[value],
      })),
      renderCell: ({ row }) => (
        <Stack sx={STACKED_CELL_SX}>
          <Typography variant="body2" noWrap>
            {eventTypeLabels[row.eventType] ?? row.eventType}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            {eventTypeCategory(row.eventType, terms)}
          </Typography>
        </Stack>
      ),
    },
    {
      field: 'suggestedAction',
      headerName: 'AI Suggestion',
      type: 'singleSelect',
      width: 160,
      ...CENTERED,
      // The empty-string member means "the AI made no recommendation"; it is an
      // item value, not a choice, so it stays off the dropdown.
      valueOptions: SUGGESTION_OPTIONS.map((value) => ({
        value,
        label: SUGGESTED_ACTION_LABEL[value],
      })),
    },
    {
      field: 'valueAtStake',
      headerName: 'Value at Stake',
      type: 'number',
      width: 150,
      ...CENTERED,
      valueGetter: (_, row) => {
        const value = Number(row.valueAtStake);
        return row.valueAtStake && Number.isFinite(value) ? value : null;
      },
      renderCell: ({ row }) =>
        row.valueAtStake ? (
          <Stack sx={STACKED_CELL_CENTERED_SX}>
            <Typography variant="body2" fontWeight={500}>
              {formatValueAtStake(row.valueAtStake, currencyCode)}
            </Typography>
            {row.valueLabel ? (
              <Typography variant="caption" color="text.secondary" noWrap>
                {row.valueLabel}
              </Typography>
            ) : null}
          </Stack>
        ) : (
          EM_DASH
        ),
    },
    {
      field: 'createdAt',
      headerName: 'Age',
      // Dated rather than text so the Filters panel offers before/after and the column sorts
      // by when it arrived, not by the string "3w" reads as.
      type: 'dateTime',
      width: 110,
      ...CENTERED,
      valueGetter: (_, row) => new Date(row.createdAt),
      renderCell: ({ row }) => formatAge(row.createdAt),
    },
    {
      field: 'actions',
      headerName: '',
      width: 150,
      sortable: false,
      filterable: false,
      hideable: false,
      disableColumnMenu: true,
      disableExport: true,
      ...CENTERED,
      renderCell: ({ row }) => (
        <Stack direction="row" spacing={0.5} alignItems="center" justifyContent="center">
          <Tooltip title="Snooze">
            <IconButton
              size="small"
              aria-label={`snooze ${displayTitle(row, terms)}`}
              onClick={(event) => {
                event.stopPropagation();
                onSnooze(row, event);
              }}
            >
              <SnoozeIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Button
            variant="text"
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              onReview(row);
            }}
          >
            Review →
          </Button>
        </Stack>
      ),
    },
  ];
}
